import React, { useMemo, useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import { Trans } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { Header, FlexMiddle, Button, PremiumModal, SupplierFreemiumPlansModal } from '@ubeya/shared-web/components';
import { responsiveShow } from '@ubeya/shared-web/style';

import privileges from '@ubeya/shared-web/privileges';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import { plansModal, premiumModal } from '@ubeya/shared/atoms/shared';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import PrivilegesContext from '@ubeya/shared-web/contexts/PrivilegesContext';
import BaseProfileMenu from '../ProfileMenu';
import DemoModal from './DemoModal';
import EndTrialModal from './EndTrialModal';
import { AccountHeaderIcons } from './AccountHeaderIcons';
import OnboardingModal from './OnboardingModal';

const Container = styled(FlexMiddle)`
  ${responsiveShow.tablet}
`;

const ProfileMenu = styled(BaseProfileMenu)`
  margin-left: 20px;
`;

const DemoButton = styled(Button).attrs(() => ({ narrow: true }))`
  background: ${({ theme }) => theme.colors.orangeChanges};
  color: ${({ theme }) => theme.colors.white};
  margin-right: 10px;
  border-radius: 15px;
`;

const TrialContainer = styled.div`
  cursor: pointer;
  margin-right: 10px;
`;

const TrialCircle = styled.span`
  background: ${(props) => (props.isAlert ? 'rgba(242, 79, 99, 0.4)' : 'rgba(14, 52, 116, 0.1)')};
  width: 20px;
  height: 20px;
  border-radius: 50%;
  text-align: center;
  display: inline-block;
  color: ${({ theme }) => theme.colors.black};
`;

const ChargeIssuesButton = styled(Button).attrs(() => ({ narrow: true }))`
  background: ${({ theme }) => theme.colors.redNegative};
  color: ${({ theme }) => theme.colors.white};
  margin-right: 10px;
`;

const AccountHeader = (props) => {
  const { t, isAccountNetworkAccessSupplier } = useContext(ConfigContext);
  const { push } = useHistory();
  const {
    accountId,
    account: { settings, invoices, isHost }
  } = useAccount();
  const showPremiumPayload = useRecoilValue(premiumModal);
  const myPrivileges = useContext(PrivilegesContext);

  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [isEndTrialModalOpen, setIsEndTrialModalOpen] = useState(false);
  const [isOnboardingModalOpen, setIsOnboardingModalOpen] = useState(false);
  const isPlansModalOpen = useRecoilValue(plansModal);

  const daysLeft = useMemo(() => {
    return moment(settings?.billing?.status?.trialEndDate).diff(moment(), 'days') + 1;
  }, [settings?.billing?.status?.trialEndDate]);

  const hasPayingIssues = useMemo(() => {
    if (
      !!myPrivileges[privileges.BILLING] &&
      ['warning', 'blocked'].includes(settings?.billing?.status?.type) &&
      invoices.find(({ billed }) => billed === false)
    ) {
      return true;
    }

    return false;
  }, [invoices, myPrivileges, settings?.billing?.status?.type]);

  const isBlocked = useMemo(() => ['blocked', 'frozen', 'unqualified'].includes(settings?.billing?.status?.type), [
    settings?.billing?.status?.type
  ]);

  const isOnboarding = useMemo(() => settings?.billing?.status?.type === 'onboarding', [
    settings?.billing?.status?.type
  ]);

  useEffect(() => {
    if (settings?.billing?.status?.type === 'migration_client') {
      return;
    }
    if (isHost) {
      return;
    }

    const checkInterval = () => {
      setIsEndTrialModalOpen(isBlocked && !window?.location?.href?.endsWith('/billing'));
      setIsOnboardingModalOpen(isOnboarding && !window?.location?.href?.endsWith('/billing'));
    };

    checkInterval();
    const interval = setInterval(checkInterval, 30000);
    return () => {
      clearInterval(interval);
    };
  }, [daysLeft, isHost, settings?.billing?.status?.type, isBlocked, isOnboarding]);

  return (
    <>
      <Header
        {...props}
        showRefreshBanner
        showLabEnvironmentBadge
        showSupplierFreemiumBanner={isAccountNetworkAccessSupplier}
        firstHeaderContent={
          <Container>
            {settings?.billing?.status?.type === 'trial' && (
              <TrialContainer onClick={() => push(`/${accountId}/billing`)}>
                <Trans i18nKey="youHaveDaysLeftFreeTrial">
                  <TrialCircle isAlert={daysLeft <= 0}>{{ days: Math.max(daysLeft, 0) }}</TrialCircle>
                </Trans>
              </TrialContainer>
            )}

            {hasPayingIssues ? (
              <>
                <ChargeIssuesButton onClick={() => push(`/${accountId}/billing`)}>
                  {t('hasChargeIssues')}
                </ChargeIssuesButton>
                <TrialContainer onClick={() => push(`/${accountId}/billing`)}>
                  You have{' '}
                  <TrialCircle isAlert={daysLeft <= 0}>
                    {Math.max(
                      0,
                      moment
                        .utc(settings?.billing?.status?.warning?.warningStartDate)
                        .add(4, 'days')
                        .diff(moment(), 'days') + 1
                    )}
                  </TrialCircle>{' '}
                  days left untill your account will be blocked
                </TrialContainer>
              </>
            ) : null}
            {!isHost && (settings?.billing?.status?.type === 'trial' || settings?.billing?.status?.type === 'pilot') ? (
              <DemoButton onClick={() => setIsDemoModalOpen(true)}>{t('scheduleDemo')}</DemoButton>
            ) : null}

            {!isHost && <AccountHeaderIcons />}

            <ProfileMenu isMobile type="admin" />
          </Container>
        }
      />
      {isDemoModalOpen && <DemoModal onClose={() => setIsDemoModalOpen(false)} />}
      {isEndTrialModalOpen && <EndTrialModal onClose={() => setIsEndTrialModalOpen(false)} />}
      {isOnboardingModalOpen && <OnboardingModal onClose={() => setIsOnboardingModalOpen(false)} />}
      {showPremiumPayload && <PremiumModal />}
      {isPlansModalOpen && <SupplierFreemiumPlansModal />}
    </>
  );
};

export default AccountHeader;
